<template>
    <div class="modal-card card">
        <div class="card-header">
            <div>
                <h4 class="card-header-title mb-0">
                    {{ isEdit ? 'Edit server' : 'Add MCP server' }}
                </h4>
                <p class="mcp-subtitle mb-0">
                    {{ isEdit
                        ? 'PAICE picks the change up on your next message.'
                        : 'Give PAICE tools from a server outside Aqfer.' }}
                </p>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="card-body">
            <p class="mcp-eyebrow">What it is</p>

            <div class="mcp-field">
                <label class="form-label" for="mcp-name">Name</label>
                <input id="mcp-name" v-model="form.name" type="text" class="form-control" maxlength="64"
                    placeholder="Acme Billing" />
                <p class="mcp-help">What you'll see in settings and in chat.</p>
            </div>

            <div class="mcp-field">
                <label class="form-label" for="mcp-description">
                    Description <span class="mcp-optional">optional</span>
                </label>
                <input id="mcp-description" v-model="form.description" type="text" class="form-control" maxlength="500"
                    placeholder="Invoices and subscription data" />
            </div>

            <hr class="mcp-divider" />

            <p class="mcp-eyebrow">Where it is</p>

            <div class="mcp-field">
                <label class="form-label" for="mcp-url">Endpoint</label>
                <input id="mcp-url" v-model="form.url" type="url" class="form-control mcp-mono"
                    placeholder="https://mcp.example.com/v1/mcp" />
                <p class="mcp-help">
                    Must start with https and be reachable on the public internet.
                    Private and internal addresses are refused.
                </p>
            </div>

            <div class="mcp-field">
                <label class="form-label" for="mcp-prefix">
                    Tool prefix <span class="mcp-optional">optional</span>
                </label>
                <div class="input-group">
                    <input id="mcp-prefix" v-model="form.toolPrefix" type="text" class="form-control mcp-mono"
                        maxlength="24" :placeholder="derivedPrefix" />
                    <span class="input-group-text mcp-mono mcp-preview">{{ prefixPreview }}</span>
                </div>
                <p class="mcp-help">
                    Added to every tool name from this server, so two servers can both
                    offer a <code>list_items</code> without clashing. Lowercase
                    letters, digits and underscores; taken from the name if left blank.
                </p>
            </div>

            <hr class="mcp-divider" />

            <p class="mcp-eyebrow">Who may use it</p>

            <div class="mcp-field">
                <label class="form-label" for="mcp-auth">Authentication</label>
                <select id="mcp-auth" v-model="form.authType" class="form-select">
                    <option v-for="option in authOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                    </option>
                </select>
                <p class="mcp-help">{{ authHelp }}</p>
            </div>

            <div v-if="form.authType === 'api_key'" class="mcp-field">
                <label class="form-label" for="mcp-key">
                    API key
                    <span class="mcp-optional">only to test</span>
                </label>
                <input id="mcp-key" v-model="form.apiKey" type="password" class="form-control mcp-mono"
                    autocomplete="off" spellcheck="false" placeholder="Paste the key from the server's own settings" />
                <p class="mcp-help">
                    <template v-if="isEdit">
                        Keys are saved from the server's own page, not here. Anything
                        entered here is used for Test connection and then discarded.
                    </template>
                    <template v-else>
                        Used to test the connection now. Save the server first, then
                        store the key on its page — that's the copy PAICE uses.
                    </template>
                </p>
            </div>

            <div v-if="form.authType === 'api_key'" class="mcp-field">
                <label class="form-label" for="mcp-ownership">Who the key belongs to</label>
                <select id="mcp-ownership" v-model="form.credentialOwnership" class="form-select"
                    :disabled="!canCreateShared">
                    <option value="per_user">Each person saves their own key</option>
                    <option value="shared">One key for everyone on this account</option>
                </select>
                <p class="mcp-help">
                    <template v-if="!canCreateShared">
                        A shared team key can only be set by an account admin.
                    </template>
                    <template v-else>
                        A shared key means one credential for the whole account, so
                        every call looks the same to the server. Per-person keys keep
                        attribution intact — prefer them where the server supports it.
                    </template>
                </p>
            </div>

            <div class="mcp-field mb-0">
                <label class="form-label" for="mcp-scope">Who can see this server</label>
                <select id="mcp-scope" v-model="form.scope" class="form-select"
                    :disabled="isEdit || !canCreateShared">
                    <option value="user">Only me</option>
                    <option value="shared">Everyone on this account</option>
                </select>
                <p class="mcp-help">
                    <template v-if="isEdit">
                        Fixed after creation — moving a server between private and
                        shared would change who its saved keys belong to.
                    </template>
                    <template v-else-if="!canCreateShared">
                        Only account admins can add a server for everyone.
                    </template>
                    <template v-else>
                        Shared servers appear for everyone on {{ cid }}
                    </template>
                </p>
            </div>

            <!-- Test result: the reason to dial before saving. -->
            <div v-if="testResult" class="mcp-test" :class="testResult.ok ? 'mcp-test-ok' : 'mcp-test-bad'">
                <i class="fa-solid mt-1" :class="testResult.ok ? 'fa-circle-check' : 'fa-circle-exclamation'"></i>
                <div>
                    <template v-if="testResult.ok">
                        <p class="mb-0">
                            Answered in {{ testResult.elapsedMs }} ms with
                            {{ testResult.toolCount }}
                            {{ testResult.toolCount === 1 ? 'tool' : 'tools' }}.
                        </p>
                        <p v-if="testResult.tools && testResult.tools.length" class="mcp-test-tools mb-0">
                            {{ testResult.tools.slice(0, 8).join(', ')
                            }}<template v-if="testResult.tools.length > 8">, …</template>
                        </p>
                    </template>
                    <p v-else class="mb-0">{{ testResult.error }}</p>
                </div>
            </div>

            <p v-if="error" class="mcp-error">{{ error }}</p>
        </div>

        <div class="card-footer d-flex justify-content-between align-items-center">
            <button class="btn btn-light" :disabled="!canTest || testing || saving" @click="test">
                <span v-if="testing" class="spinner-border spinner-border-sm me-1" role="status" style="width: 1em; height: 1em;">
                    <span class="visually-hidden">Testing</span>
                </span>
                Test connection
            </button>

            <div class="d-flex gap-2">
                <button class="btn btn-secondary" :disabled="saving" @click="cancel">Cancel</button>
                <button class="btn btn-primary" :disabled="!canSave || saving" @click="save">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status">
                        <span class="visually-hidden">Saving</span>
                    </span>
                    {{ isEdit ? 'Save changes' : 'Add server' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';

/**
 * Only the auth types the agents service can actually complete a sign-in for
 * are offered. OAuth, enterprise sign-in and AWS IAM exist in the API's enum
 * but `make_auth_provider` still refuses them, so offering them here would let
 * someone configure a server that silently never works.
 */
const AUTH_OPTIONS = [
    { value: 'none', label: 'None — the server is open' },
    { value: 'oauth_cimd', label: 'Sign in to the server (OAuth)' },
    { value: 'api_key', label: 'API key' },
    { value: 'bearer_passthrough', label: 'My Aqfer sign-in (Aqfer-operated servers only)' },
];

const AUTH_HELP = {
    none: 'Anyone who can reach the endpoint can call its tools.',
    oauth_cimd: 'Each person signs in to the server itself and grants PAICE access. '
        + 'Nothing to paste — everything about the sign-in is discovered from the '
        + 'endpoint when you connect.',
    api_key: 'A static key sent as a bearer token. Stored encrypted, never shown again.',
    bearer_passthrough: 'Forwards your Aqfer sign-in. Only for servers Aqfer runs — '
        + 'never give a third party your Aqfer token.',
};

/**
 * Ownership is not free-form: the service enforces this pairing on write.
 *
 * OAuth is per_user and cannot be anything else — tokens are identity-bound, so
 * sharing one would break attribution and the MCP spec's audience rules. The
 * service rejects `oauth_cimd` + `shared` outright.
 */
const OWNERSHIP_FOR_AUTH = {
    none: 'infrastructure',
    bearer_passthrough: 'infrastructure',
    api_key: 'per_user',
    oauth_cimd: 'per_user',
};

const PREFIX_UNSAFE = /[^a-z0-9_]+/g;

export default {
    name: 'PaiceMcpServer',
    props: {
        modalData: {
            type: Object,
            default: () => ({ mode: 'create' }),
        },
    },
    data() {
        const s = this.modalData?.server || {};
        return {
            authOptions: AUTH_OPTIONS,
            form: {
                name: s.name || '',
                description: s.description || '',
                url: s.url || '',
                toolPrefix: s.toolPrefix || '',
                authType: s.authType || 'none',
                credentialOwnership: s.credentialOwnership || 'infrastructure',
                scope: s.scope || 'user',
                // Never populated from the server — keys are write-only. Only
                // used to dial a test before one has been saved.
                apiKey: '',
            },
            saving: false,
            testing: false,
            testResult: null,
            error: '',
        };
    },
    computed: {
        isEdit() {
            return this.modalData?.mode === 'edit';
        },
        canCreateShared() {
            return !!this.modalData?.canCreateShared;
        },
        cid() {
            return this.$store.state.currentClient?.cid;
        },
        derivedPrefix() {
            return this.sanitizePrefix(this.form.name) || 'external';
        },
        effectivePrefix() {
            return this.sanitizePrefix(this.form.toolPrefix) || this.derivedPrefix;
        },
        prefixPreview() {
            return `${this.effectivePrefix}_list_items`;
        },
        authHelp() {
            return AUTH_HELP[this.form.authType] || '';
        },
        canTest() {
            if (!this.form.url.trim()) return false;
            // An api_key server with no key would only ever prove that the
            // endpoint rejects anonymous calls, which isn't worth a round trip.
            if (this.form.authType === 'api_key' && !this.form.apiKey.trim()) return false;
            // An OAuth server answers 401 to everything until the user has
            // connected, so a test before that only ever proves it is protected.
            if (this.form.authType === 'oauth_cimd') return false;
            return true;
        },
        canSave() {
            return !!this.form.name.trim() && !!this.form.url.trim();
        },
        /**
         * Availability is one client-wide switch, so the only thing a shared
         * server leaves to each person is the credential — and only when the
         * auth type is one PAICE holds per identity.
         */
        isPerUserCredential() {
            return OWNERSHIP_FOR_AUTH[this.form.authType] === 'per_user';
        },
    },
    watch: {
        /** Ownership is derived, not chosen, except for the api_key fork. */
        'form.authType': function onAuthTypeChange(next) {
            this.form.credentialOwnership = OWNERSHIP_FOR_AUTH[next] || 'per_user';
            this.testResult = null;
        },
        /** A URL edit invalidates whatever the last test proved. */
        'form.url': function onUrlChange() {
            this.testResult = null;
        },
    },
    methods: {
        ...mapActions(['hideModal', 'addToast']),
        ...mapActions('paice', ['createMcpServer', 'updateMcpServer', 'testMcpServer']),

        sanitizePrefix(value) {
            return (value || '').toLowerCase().replace(PREFIX_UNSAFE, '_').replace(/^_+|_+$/g, '');
        },

        cancel() {
            this.hideModal();
        },

        async test() {
            this.testing = true;
            this.testResult = null;
            this.error = '';
            try {
                this.testResult = await this.testMcpServer({
                    definition: {
                        url: this.form.url.trim(),
                        authType: this.form.authType,
                        apiKey: this.form.authType === 'api_key'
                            ? this.form.apiKey.trim()
                            : null,
                    },
                });
            } catch (e) {
                this.testResult = {
                    ok: false,
                    elapsedMs: 0,
                    error: this.errorMessage(e, 'Could not reach the agents service.'),
                };
            } finally {
                this.testing = false;
            }
        },

        /**
         * Scope is fixed after creation; everything else can change.
         *
         * `credentialOwnership` is sent on edit as well as create, even though
         * the form rarely shows it: the service re-checks the (authType,
         * ownership) pair against the stored row after merging, so switching a
         * server from `none` to `api_key` without sending the matching
         * ownership is rejected with an error the user cannot act on.
         *
         * The API key is never in either payload — it has its own endpoint, and
         * anything typed into this form is only ever used to dial a test.
         */
        buildPayload() {
            const common = {
                name: this.form.name.trim(),
                description: this.form.description.trim() || null,
                url: this.form.url.trim(),
                toolPrefix: this.effectivePrefix,
                authType: this.form.authType,
                credentialOwnership: this.form.credentialOwnership,
            };
            return this.isEdit ? common : { ...common, scope: this.form.scope };
        },

        async save() {
            this.saving = true;
            this.error = '';
            try {
                const saved = this.isEdit
                    ? await this.updateMcpServer({
                        serverId: this.modalData.server.serverId,
                        server: this.buildPayload(),
                    })
                    : await this.createMcpServer(this.buildPayload());

                if (typeof this.modalData.onSaved === 'function') {
                    this.modalData.onSaved(saved);
                }
                this.addToast({
                    type: 'success',
                    message: this.isEdit ? 'Server saved' : 'Server added',
                });
                this.hideModal();
            } catch (e) {
                this.error = this.errorMessage(e, 'Could not save the server.');
            } finally {
                this.saving = false;
            }
        },

        errorMessage(e, fallback) {
            const detail = e?.response?.data?.detail;
            if (typeof detail === 'string' && detail) return detail;
            // FastAPI validation errors arrive as a list of {loc, msg}.
            if (Array.isArray(detail) && detail.length) {
                return detail.map((d) => d.msg).filter(Boolean).join('; ') || fallback;
            }
            return e?.message || fallback;
        },
    },
};
</script>

<style scoped>
.mcp-subtitle {
    font-size: 0.75rem;
    color: #95aac9;
}

/* Run-in heading for each of the form's three questions. Matches the eyebrow
   on the settings panel so the modal reads as part of the same page. */
.mcp-eyebrow {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #95aac9;
    margin: 0 0 0.75rem;
}

.mcp-field {
    margin-bottom: 1.1rem;
}

/* The prefix preview is a worked example of the result, not an editable half of
   the input — hold it back from the field it explains. */
.mcp-preview {
    color: #6e84a3;
    background: #f9fbfd;
}

.mcp-help {
    margin: 0.3rem 0 0;
    font-size: 0.72rem;
    color: #95aac9;
}

.mcp-help code {
    font-size: 0.7rem;
    color: #6e84a3;
    background: none;
    padding: 0;
}

.mcp-optional {
    font-size: 0.65rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b1c2d9;
    margin-left: 0.25rem;
}

.mcp-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.8rem;
}

.mcp-divider {
    margin: 1.35rem 0;
    border-color: #edf2f9;
}

.mcp-test {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 1.25rem;
    padding: 0.7rem 0.85rem;
    border-radius: 0.5rem;
    font-size: 0.78rem;
}

.mcp-test-ok {
    background: #e6f6ee;
    color: #00854a;
}

.mcp-test-bad {
    background: #fdeaee;
    color: #b52b45;
}

.mcp-test-tools {
    margin-top: 0.25rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.7rem;
    opacity: 0.85;
    word-break: break-word;
}

.mcp-error {
    margin: 1rem 0 0;
    font-size: 0.78rem;
    color: #e63757;
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

/* Unscoped: `body.dark-mode` sits on an ancestor of the modal root, which a
   scoped selector cannot reach. Bootstrap form controls, the card and the
   buttons are covered globally in _user.scss — only the `mcp-*` names
   authored here need restating. */
body.dark-mode {

    .mcp-subtitle,
    .mcp-eyebrow,
    .mcp-help {
        color: $dark-text-muted !important;
    }

    .mcp-help code {
        color: $dark-text-secondary !important;
    }

    .mcp-optional {
        color: #5c6675 !important;
    }

    .mcp-preview {
        background: $dark-body-bg !important;
        color: $dark-text-muted !important;
    }

    .mcp-divider {
        border-color: $dark-border-color !important;
    }

    /* Translucent tints: the light theme's solid pastels read as bright blocks
       against a near black card. */
    .mcp-test-ok {
        background: rgba(0, 217, 126, 0.12) !important;
        color: #5fd3a3 !important;
    }

    .mcp-test-bad {
        background: rgba(230, 55, 87, 0.14) !important;
        color: #f08a9c !important;
    }

    .mcp-error {
        color: #f08a9c !important;
    }
}
</style>
